'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { FormLabel, Button, Divider, IconButton, Link, Skeleton, FormControl } from '@mui/material';
import { useTranslations } from 'next-intl';
import AutoCompleteGooglePlaces from 'components/commercetools-ui/atoms/auto-complete-google-places';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import { CloseIcon } from 'components/icons/closeIcon';
import { useSitePreferences } from 'context/site-preferences';
import useTrackEvent from 'helpers/hooks/gtm/useTrackEvent';
import { getCurrentLocationData, getFinalLocation } from 'helpers/location';
import { STATE_ABBREVIATIONS } from 'helpers/location/constant';
import { LocationInfo, useLocationStore } from 'stores/location';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  isUpdatingLocation: boolean;
};

export default function LocationDrawer({ isOpen, isUpdatingLocation, onClose }: Props) {
  const [location, setLocation] = useState<LocationInfo | undefined>();
  const [currentLocationError, setCurrentLocationError] = useState('');
  const updateLocation = useLocationStore((state) => state.updateLocation);
  const trackEvent = useTrackEvent();
  const translate = useTranslations();
  const isLoadingGoogleMap = useLocationStore((state) => state.isLoadingGoogleMap);
  const [submitAfterOptionsLoaded, setSubmitAfterOptionsLoaded] = useState(false);
  const options = useLocationStore((s) => s.options);
  const setOptions = useLocationStore((s) => s.setOptions);
  const isOptionsLoading = useLocationStore((s) => s.isOptionsLoading);
  const setOptionsLoading = useLocationStore((s) => s.setOptionsLoading);
  const locationInput = useLocationStore((s) => s.locationInput);
  const setLocationInput = useLocationStore((s) => s.setLocationInput);
  const { stateAbbreviations } = useSitePreferences() || {};
  const stateValues = useMemo(() => {
    try {
      return JSON.parse(stateAbbreviations ?? '') ?? STATE_ABBREVIATIONS;
    } catch {
      return STATE_ABBREVIATIONS;
    }
  }, [stateAbbreviations]);

  useEffect(() => {
    setCurrentLocationError('');
    setOptionsLoading(false);
    setSubmitAfterOptionsLoaded(false);
    if (!isOpen) {
      setLocation(undefined); // Reset location when closing the drawer
      setOptions([]);
      setLocationInput('');
      setOptionsLoading(false);
    }
  }, [isOpen, setLocationInput, setOptions, setOptionsLoading]);

  const useCurrentLocation = useCallback(async () => {
    try {
      const location = await getCurrentLocationData();
      const { city, lat, lng, state, countryFullName } = location;
      const loc = [city, state].filter((x) => x).join(', ');
      updateLocation({ lat, lng, location: loc, state, countryFullName });
      trackEvent({
        action: 'location_used_current',
        label: `${city}, ${state}`,
      });
      setCurrentLocationError('');
      onClose();
    } catch (error) {
      if (!location) return setCurrentLocationError(translate('location.enable-location-service'));
      setCurrentLocationError(translate('location.unable-retrieve-your-location'));
      console.log('Error while trying to use current location', error);
    }
  }, [updateLocation, trackEvent, onClose, location, translate]);

  const updateUserLocation = useCallback(() => {
    if (!location) {
      setCurrentLocationError(translate('location.unable-retrieve-your-location'));
      return;
    }
    setCurrentLocationError('');
    if (location) {
      updateLocation(location);
      setOptions([]);
      trackEvent({
        action: isUpdatingLocation ? 'location_updated' : 'location_added',
        label: location?.location,
      });
      onClose();
    }
  }, [isUpdatingLocation, location, onClose, setOptions, trackEvent, translate, updateLocation]);

  useEffect(() => {
    if (submitAfterOptionsLoaded && !isOptionsLoading) {
      updateUserLocation();
      setSubmitAfterOptionsLoaded(false);
    }
  }, [isOptionsLoading, submitAfterOptionsLoaded, updateUserLocation]);

  //  On button Click if options already loaded, pick first immediately
  const getFirstOptionSelectedForLocation = useCallback(async () => {
    if (options.length > 0) {
      // try to find an option matching the input text
      const matched: any = options.find(
        (opt: any) => opt.structured_formatting?.main_text?.toLowerCase() === locationInput?.toLowerCase(),
      );

      if (matched) {
        const loc = await getFinalLocation(matched, stateValues);

        setLocation(loc);
        setOptions([]);
        updateLocation(loc);

        trackEvent({
          action: isUpdatingLocation ? 'location_updated' : 'location_added',
          label: loc.location,
        });

        onClose();
      } else {
        // input did not match any autocomplete suggestion
        setCurrentLocationError(translate('location.unable-retrieve-your-location'));
      }
    } else {
      setCurrentLocationError(translate('location.unable-retrieve-your-location'));
      return;
    }
  }, [
    isUpdatingLocation,
    locationInput,
    onClose,
    options,
    setOptions,
    stateValues,
    trackEvent,
    translate,
    updateLocation,
  ]);

  const handleUpdateLocation = useCallback(
    (e: React.FormEvent | undefined) => {
      e?.preventDefault();
      e?.stopPropagation();

      if (isOptionsLoading) {
        setSubmitAfterOptionsLoaded(true);
        return;
      }

      if (!location) {
        getFirstOptionSelectedForLocation();
        return;
      }
      updateUserLocation();
    },
    [getFirstOptionSelectedForLocation, isOptionsLoading, location, updateUserLocation],
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} disableOutsideClick={!isUpdatingLocation}>
      {/* Scrollable Content */}
      {!isLoadingGoogleMap && (
        <div className="flex flex-col gap-8">
          <Skeleton variant="text" width="100%" height={50} />
          <Skeleton variant="rectangular" width="100%" height={50} />
          <Skeleton variant="text" width="80%" height={50} />
        </div>
      )}
      {isLoadingGoogleMap && (
        <form onSubmit={handleUpdateLocation} className="flex h-full flex-col justify-between">
          <div className="grow overflow-y-auto">
            <div className="mb-16 flex items-center justify-between">
              <span className="global-text-sm-semi-degular text-primary">
                {isUpdatingLocation
                  ? translate('location.change-location')
                  : translate('location.find-store-to-get-started')}
              </span>
              {isUpdatingLocation && (
                <IconButton onClick={onClose}>
                  <CloseIcon sx={{ color: 'var(--color-black)' }} />
                </IconButton>
              )}
            </div>

            <div className="flex flex-col gap-16">
              {!isUpdatingLocation && (
                <p className="global-text-sm text-[var(--color-secondary)]">
                  {translate('location.not-updating-location-message')}
                </p>
              )}
              <FormLabel
                htmlFor="location-autocomplete"
                required
                sx={{
                  '& .MuiFormLabel-asterisk': {
                    color: 'red',
                  },
                  fontFamily: 'var(--font-degular-text)',
                  fontWeight: 600,
                }}
                className="text-[var(--color-secondary)]"
              >
                {isUpdatingLocation
                  ? translate('location.enter-zip-code-or-city')
                  : translate('location.updating-location-message')}
              </FormLabel>
              <FormControl>
                <AutoCompleteGooglePlaces
                  onChange={setLocation}
                  onCurrentLocationError={setCurrentLocationError}
                  errorMessage={currentLocationError}
                  onUpdateLocation={handleUpdateLocation}
                />
              </FormControl>
              <div className="flex flex-col gap-8">
                <div className="flex items-center gap-5">
                  <PlaceOutlinedIcon />
                  <Link component="button" type="button" variant="body2" onClick={useCurrentLocation}>
                    {translate('location.use-my-current-location')}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky Footer */}
          <div>
            <Divider sx={{ borderColor: 'var(--color-Outline-Variant)' }} />
            <div className="flex w-full flex-row justify-between gap-12 p-16">
              {isUpdatingLocation && (
                <Button fullWidth variant="outlined" onClick={onClose} type="button">
                  {translate('button.cancel')}
                </Button>
              )}
              <Button
                loading={submitAfterOptionsLoaded}
                fullWidth
                variant="contained"
                type="submit"
                data-testid="location-drawer-submit"
              >
                {isUpdatingLocation ? translate('button.update') : translate('button.continue')}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Drawer>
  );
}
